import * as random from "lib0/random";
import * as awarenessProtocol from "y-protocols/awareness";
import * as Y from "yjs";
import { joinPaths, parsePaths, splitParentPath } from "../util/path";

export const SERVER_ID = 2 ** 32;
export const ACTIONS_ID = 2 ** 32 + 1;

export const CONTENT_TYPE = {
  directory: "inode/directory; charset=binary",
  symlink: "text/plain; charset=utf-8",
  fileBinary: "application/octet-stream; charset=binary",
  fileDrawing: "application/tldraw; charset=utf-8",
  fileEmpty: "inode/x-empty; charset=binary",
  fileText: "text/plain; charset=utf-8",
};
export const IS_EXECUTABLE_DEFAULT = {
  directory: true,
  symlink: false,
  fileBinary: false,
  fileDrawing: false,
  fileEmpty: false,
  fileText: false,
};

export type FileType = "directory" | "symlink" | "file";
export type Metadata = { fileType: FileType; contentType: string; isExecutable: boolean };
export type DirectoryEntry = { guid: string; metadata: Metadata };
export type DirectoryEntries = { [name: string]: DirectoryEntry };

export type ActionSpec =
  | { actionId: string; actionType: "copy"; guid: string; guidParentDest: string }
  | { actionId: string; actionType: "delete"; guid: string }
  | { actionId: string; actionType: "unpack"; pathDest: string; path: string };
export type ActionStatusSpec = { actionId: string; clientId: number; state: "pending" | "started" | "done" };
export type ActionsObject = { [actionId: string]: ActionSpec | undefined };
export type ActionsStatusObject = { [actionId: string]: ActionStatusSpec | undefined };

export class YFSClient extends Y.Doc {
  awareness: awarenessProtocol.Awareness;
  fsName: string;
  urlClient: URL;
  urlServerHTTP: URL;
  urlServerWS: URL;
  token: string | undefined;
  updatedAccessAt: string | undefined;
  guidsUsedDocs: Set<string>;

  constructor({
    fsName,
    baseUrlClient,
    baseUrlServerHTTP,
    baseUrlServerWS,
    token,
    updatedAccessAt,
  }: {
    fsName: string;
    baseUrlClient: string;
    baseUrlServerHTTP: string;
    baseUrlServerWS: string;
    token?: string;
    updatedAccessAt?: string;
  }) {
    super({ guid: "GUID_FILESYSTEM" });

    this.awareness = new awarenessProtocol.Awareness(this);
    this.fsName = fsName;
    this.urlClient = new URL(baseUrlClient);
    this.urlServerHTTP = new URL(baseUrlServerHTTP);
    this.urlServerWS = new URL(baseUrlServerWS);
    this.token = token;
    this.updatedAccessAt = updatedAccessAt;
    this.guidsUsedDocs = new Set();

    const handleSubdocsRoot = (({ added }: { added: Set<Y.Doc>; removed: Set<Y.Doc>; loaded: Set<Y.Doc> }) => {
      if (this.isSynced) return;
      for (const d of added) {
        if (d.guid === "GUID_ROOT") {
          this.emit("sync", [true, this]);
          return;
        }
      }
    }).bind(this);
    this.on("subdocs", handleSubdocsRoot);

    const handleAwarenessUpdate = (({ added, updated }: { added: any; updated: any; removed: any }) => {
      // Remove actions which are done (based on actions status)
      for (const clientId of [...added, ...updated]) {
        // Status comes only from the server
        if (clientId !== SERVER_ID) continue;
        const actionsStatus = this.getActionsStatus();
        const actions = this.getActions();
        for (const actionId of Object.keys(actions)) {
          const state = actionsStatus?.[actionId]?.state;
          if (state === "done") {
            const actionsDone = { ...this.getActions() };
            delete actionsDone[actionId];
            this.awareness.setLocalState({ ...this.awareness.getLocalState(), actions: actionsDone });
          }
        }
      }
    }).bind(this);
    this.awareness.on("update", handleAwarenessUpdate);
  }

  // In all methods:
  // 1. Documents passed as arguments MUST be loaded.
  // 2. Documents returned MAY NOT be loaded.

  // ---------------------------------------------------------------------------
  // DOCS - shared between YFS and YFSClient
  // ---------------------------------------------------------------------------

  getDoc(guid: string) {
    if (guid === this.guid) return this;
    const ydoc = this.getMap("GUID_TO_DOC").get(guid) as Y.Doc | undefined;
    return ydoc;
  }

  getDocParent(guid: string) {
    if (guid === "GUID_ROOT") return this;
    const guidParent = this.getMap("GUID_TO_GUID_PARENT").get(guid) as string | undefined;
    if (!guidParent) return undefined;
    const ydocParent = this.getMap("GUID_TO_DOC").get(guidParent) as Y.Doc | undefined;
    if (!ydocParent) throw new Error("ydocParent is missing");
    return ydocParent;
  }

  getDocChild(ydocParent: Y.Doc, name: string) {
    const guid = ydocParent.getMap("NAME_TO_GUID_CHILD").get(name) as string | undefined;
    if (!guid) return undefined;
    const ydoc = this.getMap("GUID_TO_DOC").get(guid) as Y.Doc | undefined;
    return ydoc;
  }

  // ---------------------------------------------------------------------------
  // DOCS - only in YFSClient
  // ---------------------------------------------------------------------------

  async getDocByPath(path: string) {
    if (!path) return undefined;
    const ydocRoot = this.getDoc("GUID_ROOT");
    if (!ydocRoot) throw new Error("ydocRoot is missing");
    let ydoc: Y.Doc | undefined = ydocRoot;
    const pathParts = parsePaths(path);
    for (let i = 0; ydoc && i < pathParts.length; i += 1) {
      ydoc.load();
      await ydoc.whenSynced;
      ydoc = this.getDocChild(ydoc, pathParts[i]!);
    }
    return ydoc;
  }

  getName(guid: string) {
    const ydocParent = this.getDocParent(guid);
    if (!ydocParent) return undefined;
    if (ydocParent.guid === this.guid) return undefined;
    if (!ydocParent.isLoaded) return undefined;
    const name = ydocParent.getMap("GUID_CHILD_TO_NAME").get(guid) as string | undefined;
    return name;
  }

  async getPath(guid: string) {
    let guidCurrent = guid;
    const pathParts = [];
    while (true) {
      const ydocParent = this.getDocParent(guidCurrent);
      if (!ydocParent) return undefined;
      if (ydocParent.guid === this.guid) break;
      ydocParent.load();
      await ydocParent.whenSynced;
      const name = ydocParent.getMap("GUID_CHILD_TO_NAME").get(guidCurrent) as string | undefined;
      if (!name) return undefined;
      pathParts.push(name);
      guidCurrent = ydocParent.guid;
    }
    pathParts.reverse();
    const path = joinPaths(...pathParts);
    return path;
  }

  getPathCurrent() {
    if (typeof window === "undefined") return undefined;
    if (!window.location.href.startsWith(this.urlClient.href)) return undefined;
    const pathname = new URL(window.location.href).pathname;
    if (!pathname.startsWith(this.urlClient.pathname)) return undefined;
    const pathEncoded = pathname.slice(this.urlClient.pathname.length);
    return decodeURIComponent(pathEncoded) || "/";
  }

  getPathFromUrl(url: string) {
    if (!url.startsWith(this.urlClient.href)) return undefined;
    const pathname = new URL(url).pathname;
    if (!pathname.startsWith(this.urlClient.pathname)) return undefined;
    const pathEncoded = pathname.slice(this.urlClient.pathname.length);
    return decodeURIComponent(pathEncoded) || "/";
  }

  // Use Next.js Link+router to encode URLs or this method
  // DON'T USE `new URL` since it doesn't encode '%'
  getUrlFromPath(path: string) {
    // This href never ends with '/'
    return this.urlClient.href + joinPaths(...parsePaths(path).map(encodeURIComponent));
  }

  async resolvePathDest(ydoc: Y.Doc, pathDest: string) {
    if (pathDest.startsWith("/")) return pathDest;
    const pathParent = await this.getPath(ydoc.guid);
    if (!pathParent) return undefined;
    return joinPaths(pathParent, pathDest);
  }

  async validateDocDest(pathParentDest: string, nameDest: string) {
    const ydocParentDest = await this.getDocByPath(pathParentDest);
    if (!ydocParentDest) {
      return "destination-not-present" as const;
    }

    ydocParentDest.load();
    await ydocParentDest.whenSynced;

    const ydocParentFileType = this.getDocMetadataFileType(ydocParentDest.guid);
    if (ydocParentFileType !== "directory") {
      return "destination-not-directory" as const;
    }

    const ydocDest = this.getDocChild(ydocParentDest, nameDest);
    if (ydocDest) {
      return "path-already-present" as const;
    }

    return ydocParentDest;
  }

  clientCreateDocChild(ydocParent: Y.Doc, name: string, metadata: Metadata, docOpts: any) {
    const ydoc = new Y.Doc({ shouldLoad: false, ...docOpts });
    this.transact(() => {
      this.getMap("GUID_TO_DOC").set(ydoc.guid, ydoc);
      this.getMap("GUID_TO_GUID_PARENT").set(ydoc.guid, ydocParent.guid);
      ydocParent.transact(() => {
        ydocParent.getMap("NAME_TO_GUID_CHILD").set(name, ydoc.guid);
        ydocParent.getMap("GUID_CHILD_TO_NAME").set(ydoc.guid, name);
        ydocParent.getMap("GUID_CHILD_TO_FILE_TYPE").set(ydoc.guid, metadata.fileType);
        ydocParent.getMap("GUID_CHILD_TO_CONTENT_TYPE").set(ydoc.guid, metadata.contentType);
        ydocParent.getMap("GUID_CHILD_TO_IS_EXECUTABLE").set(ydoc.guid, metadata.isExecutable);
      });
    });
    return ydoc;
  }

  clientMoveDocChild(ydocParent: Y.Doc, name: string, ydocParentDest: Y.Doc, nameDest: string) {
    const guid = ydocParent.getMap("NAME_TO_GUID_CHILD").get(name) as string | undefined;
    if (guid == undefined) throw new Error("guid is missing");
    const fileType = ydocParent.getMap("GUID_CHILD_TO_FILE_TYPE").get(guid) as FileType | undefined;
    if (fileType == undefined) throw new Error("fileType is missing");
    const contentType = ydocParent.getMap("GUID_CHILD_TO_CONTENT_TYPE").get(guid) as string | undefined;
    if (contentType == undefined) throw new Error("contentType is missing");
    const isExecutable = ydocParent.getMap("GUID_CHILD_TO_IS_EXECUTABLE").get(guid) as boolean | undefined;
    if (isExecutable == undefined) throw new Error("isExecutable is missing");
    this.transact(() => {
      this.getMap("GUID_TO_GUID_PARENT").set(guid, ydocParentDest.guid);
      ydocParent.transact(() => {
        ydocParent.getMap("NAME_TO_GUID_CHILD").delete(name);
        ydocParent.getMap("GUID_CHILD_TO_NAME").delete(guid);
        ydocParent.getMap("GUID_CHILD_TO_FILE_TYPE").delete(guid);
        ydocParent.getMap("GUID_CHILD_TO_CONTENT_TYPE").delete(guid);
        ydocParent.getMap("GUID_CHILD_TO_IS_EXECUTABLE").delete(guid);
        // When dest parent is the same as the current, this ensures operation is atomic
        ydocParentDest.transact(() => {
          ydocParentDest.getMap("NAME_TO_GUID_CHILD").set(nameDest, guid);
          ydocParentDest.getMap("GUID_CHILD_TO_NAME").set(guid, nameDest);
          ydocParentDest.getMap("GUID_CHILD_TO_FILE_TYPE").set(guid, fileType);
          ydocParentDest.getMap("GUID_CHILD_TO_CONTENT_TYPE").set(guid, contentType);
          ydocParentDest.getMap("GUID_CHILD_TO_IS_EXECUTABLE").set(guid, isExecutable);
        });
      });
    });
  }

  unloadUnusedDocs() {
    const guidsUsedDocsTree = new Set<string>();
    for (const guidUsed of this.guidsUsedDocs) {
      let guid = guidUsed;
      guidsUsedDocsTree.add(guid);
      while (true) {
        const guidParent = this.getMap("GUID_TO_GUID_PARENT").get(guid) as string | undefined;
        if (!guidParent) break;
        if (guidParent === this.guid) break;
        guid = guidParent;
        guidsUsedDocsTree.add(guid);
      }
    }
    const loadedUnusedDocs = new Set([...this.subdocs].filter((d) => d.isLoaded && !guidsUsedDocsTree.has(d.guid)));
    this.transact(() => {
      for (const d of loadedUnusedDocs) {
        d.destroy();
      }
    });
  }

  // ---------------------------------------------------------------------------
  // ACTIONS
  // ---------------------------------------------------------------------------

  generateActionId() {
    const actionId = `action-${random.uint53()}`;
    return actionId;
  }

  getActions() {
    const actions = (this.awareness.getLocalState()?.["actions"] ?? {}) as ActionsObject;
    return actions;
  }

  getActionsStatus() {
    const actionsStatus = (this.awareness.getStates().get(SERVER_ID)?.["actionsStatus"] ?? {}) as ActionsStatusObject; // prettier-ignore
    return actionsStatus;
  }

  addActions(actions: ActionSpec[]) {
    const actionsOld = this.getActions();
    const actionsNew = { ...actionsOld };
    for (const action of actions) actionsNew[action.actionId] = action;
    this.awareness.setLocalStateField("actions", actionsNew);
  }

  removeActions(actionIds: string[]) {
    const actionsOld = this.getActions();
    const actionsNew = { ...actionsOld };
    for (const actionId of actionIds) delete actionsNew[actionId];
    this.awareness.setLocalStateField("actions", actionsNew);
  }

  // ---------------------------------------------------------------------------
  // METADATA
  // ---------------------------------------------------------------------------

  getDocMetadataFileType(guid: string) {
    if (guid === "GUID_ROOT") return "directory";
    const ydocParent = this.getDocParent(guid);
    if (!ydocParent) return undefined;
    if (!ydocParent.isLoaded) return undefined;
    const fileType = ydocParent.getMap("GUID_CHILD_TO_FILE_TYPE").get(guid) as FileType | undefined;
    return fileType;
  }

  getDocMetadataContentType(guid: string) {
    if (guid === "GUID_ROOT") return "directory";
    const ydocParent = this.getDocParent(guid);
    if (!ydocParent) return undefined;
    if (!ydocParent.isLoaded) return undefined;
    const contentType = ydocParent.getMap("GUID_CHILD_TO_CONTENT_TYPE").get(guid) as string | undefined;
    return contentType;
  }

  getDocMetadataIsExecutable(guid: string) {
    if (guid === "GUID_ROOT") return IS_EXECUTABLE_DEFAULT.directory;
    const ydocParent = this.getDocParent(guid);
    if (!ydocParent) return undefined;
    if (!ydocParent.isLoaded) return undefined;
    const isExecutable = ydocParent.getMap("GUID_CHILD_TO_IS_EXECUTABLE").get(guid) as boolean | undefined;
    return isExecutable;
  }

  static isContentTypeBinary(contentType: string | undefined) {
    if (!contentType) return undefined;
    // Empty files are considered non-binary
    if (contentType === CONTENT_TYPE.fileEmpty) return false;
    return contentType.endsWith("; charset=binary");
  }

  static isContentTypeDrawing(contentType: string | undefined) {
    if (!contentType) return undefined;
    return contentType === CONTENT_TYPE.fileDrawing;
  }

  // ---------------------------------------------------------------------------
  // DATA
  // ---------------------------------------------------------------------------

  getDocDataDirEntries(ydoc: Y.Doc) {
    const dirEntries = {} as DirectoryEntries;
    for (const [name, guid] of ydoc.getMap("NAME_TO_GUID_CHILD").entries() as IterableIterator<[string, string]>) {
      const fileType = this.getDocMetadataFileType(guid);
      if (fileType == undefined) continue;
      const contentType = this.getDocMetadataContentType(guid);
      if (contentType == undefined) continue;
      const isExecutable = this.getDocMetadataIsExecutable(guid);
      if (isExecutable == undefined) continue;
      const metadata = { fileType, contentType, isExecutable };
      dirEntries[name] = { guid, metadata };
    }
    return dirEntries;
  }

  getDocDataFileText(ydoc: Y.Doc) {
    return ydoc.getText("DATA_TEXT");
  }

  // ---------------------------------------------------------------------------
  // HREFS
  // ---------------------------------------------------------------------------

  getHrefWebSocket(q: URLSearchParams = new URLSearchParams()) {
    const u = new URL(joinPaths(this.urlServerWS.pathname, "/v1/connect"), this.urlServerWS.origin);
    if (this.updatedAccessAt) q.set("cacheKey", `updatedAccessAt=${this.updatedAccessAt}`);
    u.search = q.toString();
    return u.href;
  }

  getHrefEntry(q: URLSearchParams = new URLSearchParams(), p: string = "") {
    const u = new URL(joinPaths(this.urlServerHTTP.pathname, "v1/edit/entry", p), this.urlServerHTTP.origin);
    if (this.token) q.set("token", this.token);
    if (this.updatedAccessAt) q.set("cacheKey", `updatedAccessAt=${this.updatedAccessAt}`);
    u.search = q.toString();
    return u.href;
  }

  getHrefArchive(q: URLSearchParams = new URLSearchParams(), p: string = "") {
    const u = new URL(joinPaths(this.urlServerHTTP.pathname, "v1/edit/archive", p), this.urlServerHTTP.origin);
    if (this.token) q.set("token", this.token);
    if (this.updatedAccessAt) q.set("cacheKey", `updatedAccessAt=${this.updatedAccessAt}`);
    u.search = q.toString();
    return u.href;
  }

  getHrefPutFile(q: URLSearchParams = new URLSearchParams()) {
    const u = new URL(joinPaths(this.urlServerHTTP.pathname, "v1/edit/file"), this.urlServerHTTP.origin);
    if (this.token) q.set("token", this.token);
    if (this.updatedAccessAt) q.set("cacheKey", `updatedAccessAt=${this.updatedAccessAt}`);
    u.search = q.toString();
    return u.href;
  }

  getHrefImportFiles(q: URLSearchParams = new URLSearchParams()) {
    const u = new URL(joinPaths(this.urlServerHTTP.pathname, "v1/edit/import"), this.urlServerHTTP.origin);
    if (this.token) q.set("token", this.token);
    if (this.updatedAccessAt) q.set("cacheKey", `updatedAccessAt=${this.updatedAccessAt}`);
    u.search = q.toString();
    return u.href;
  }

  // ---------------------------------------------------------------------------
  // IMPORT
  // ---------------------------------------------------------------------------

  async handleFiles(pathParentDest: string, fileList: FileList | File[] | null) {
    if (!fileList) return;
    const files = fileList instanceof FileList ? Array.from(fileList) : fileList;
    if (!(files.length > 0)) return;
    for (const file of files) {
      try {
        const _name = file.webkitRelativePath || (file as any).path || file.name;
        const _path = joinPaths(pathParentDest, _name);
        const _href = this.getHrefPutFile(new URLSearchParams({ ts: Date.now().toString(), path: _path }));
        const response = await fetch(_href, { method: "PUT", body: file });
        if (response.ok) {
          console.log(`file upload [${file.name}]: OK`);
        } else {
          console.error(`file upload [${file.name}]: FAIL ${response.statusText}`);
        }
      } catch (error) {
        console.error(`file upload [${file.name}]: ERROR ${error}`);
      }
    }
  }

  async handleUris(pathParentDest: string, uriList: string[], internalOperation: "copy" | "move") {
    if (!(uriList.length > 0)) return;

    const urisInternal = [];
    const urisExternal = [];
    for (const uri of uriList) {
      try { new URL(uri); } catch { continue; } // prettier-ignore
      if (uri.startsWith(this.urlClient.href)) {
        urisInternal.push(uri);
      } else {
        urisExternal.push(uri);
      }
    }

    if (urisInternal.length > 0) {
      if (internalOperation === "copy") {
        await this.copyUris(pathParentDest, urisInternal);
      } else if (internalOperation === "move") {
        await this.moveUris(pathParentDest, urisInternal);
      }
    }

    if (urisExternal.length > 0) {
      await this.importUris(pathParentDest, urisExternal);
    }
  }

  async importUris(pathParentDest: string, uriList: string[]) {
    const q = new URLSearchParams();
    q.set("path", pathParentDest);
    for (const uri of uriList) {
      q.append("href", uri);
    }
    await fetch(this.getHrefImportFiles(q), { method: "PUT" });
  }

  async copyUris(pathParentDest: string, uriList: string[]) {
    const paths = uriList.map((u) => this.getPathFromUrl(u)).filter(Boolean) as string[];
    const docs = await Promise.all(paths.map((p) => this.getDocByPath(p)));
    const guids = docs.filter(Boolean).map((d) => d!.guid);

    const ydocParentDest = await this.getDocByPath(pathParentDest);
    if (ydocParentDest) {
      const guidParentDest = ydocParentDest.guid;
      this.addActions(
        guids.map((guid) => {
          const actionId = this.generateActionId();
          return { actionId, actionType: "copy", guid, guidParentDest };
        })
      );
    }
  }

  async moveUris(pathParentDest: string, uriList: string[]) {
    for (const url of uriList) {
      // We assume that we're moving files within the same filesystem
      const pathSrc = this.getPathFromUrl(url);
      if (!pathSrc) continue;
      const [pathParentSrc, name] = splitParentPath(pathSrc);

      // Validate file path present
      if (!name) continue;

      // Cannot move dir into itself or its descendant
      if ((pathParentDest + "/").startsWith(pathSrc + "/")) continue;

      const ydocParentSrc = await this.getDocByPath(pathParentSrc);

      // Validate source directory exists
      if (!ydocParentSrc) continue;

      ydocParentSrc.load();
      await ydocParentSrc.whenSynced;

      const validateResult = await this.validateDocDest(pathParentDest, name);
      if (validateResult === "destination-not-present") continue;
      if (validateResult === "destination-not-directory") continue;
      if (validateResult === "path-already-present") continue;

      const ydocParentDest = validateResult;
      this.clientMoveDocChild(ydocParentSrc, name, ydocParentDest, name);
    }
  }
}
